import { WorkspaceElement, Position, Size } from 'types';
import { Store } from 'stores/type';
import { reappend } from 'utils';
import { intersects as checkIntersection } from 'helpers/drag';
import { generateId } from 'helpers/id';
import { workspaceElement } from 'models/workspace-element';
import { WORKSPACE_ID, LIBRARY_ID } from 'constants/drag';
import EventsManager from 'managers/events.manager';


const ADD_TO_WORKSPACE_MARGIN = 20;
const CLONE_MARGIN = 10;

class WorkspaceManager {
  private $store!: Store;

  public init(store: Store) {
    this.$store = store;

    EventsManager.on('ELEMENT_CLONE', (id: string) => this.onCloned(id));
  }

  // reappend cloned element so that it appears on top
  public onCloned(id: WorkspaceElement['id']) {
    const element = this.getWorkspaceBoxById(id);
    if (!element) return;

    reappend(element);
    const box = (element as any).__vue__;
    box.onClone();
  }
  public getCloneDirections(position: Position, dimensions: Size) {
    let x = 1;
    let y = -1;

    if (position.x + dimensions.width + CLONE_MARGIN >= this.workspacePositionRight) {
      x *= -1;
    }

    if (position.y <= CLONE_MARGIN) {
      y *= -1;
    }

    return { x, y };
  }

  public addToWorkspace(elementId: WorkspaceElement['elementId']) {
    this.$store.dispatch('workspaceElementsAdd', [{
      ...workspaceElement,
      id: generateId(elementId),
      elementId,
      position: this.calculateNewElementPosition(),
    }]);
  }

  // check row by row starting from middle
  // from workspacePositionRight to left
  private calculateNewElementPosition() {
    const width = this.workspaceBoxDimensions.width + ADD_TO_WORKSPACE_MARGIN;
    const height = this.workspaceBoxDimensions.height + ADD_TO_WORKSPACE_MARGIN;

    const startingX = this.workspacePositionRight - width;
    const startingY = (this.height / 2) - (height / 2);
    let dY = 0;
    let y = startingY;
    while (y > 0 && y < this.height) {
      for (let x = startingX; x > 0; x -= width) {
        if (this.isEmptyPosition({ x, y })) {
          return { x, y };
        }
      }

      dY *= -1;
      if (dY >= 0) {
        dY += height;
      }

      y = startingY + dY;
    }

    return { x: startingX, y: startingY };
  }
  private isEmptyPosition(position: Position) {
    const droppables = this.$store.getters.droppables;
    return !droppables.some((droppable) =>
      checkIntersection(
        droppable.position,
        this.workspaceBoxDimensions,
        position,
        this.workspaceBoxDimensions,
      ));
  }

  public getWorkspaceBoxById(id: string) {
    const element = document.querySelector(`#${ WORKSPACE_ID } [data-id="${ id }"]`);
    if (!element) return null;

    return element as HTMLElement;
  }

  get workspacePositionRight() {
    const libraryDroppable = this.$store.getters.droppableStaticById(LIBRARY_ID);
    return libraryDroppable!.position.x;
  }

  get height() {
    return window.innerHeight;
  }
  get workspaceBoxDimensions() {
    return this.$store.getters.workspaceBoxDimensions;
  }
}

export default new WorkspaceManager();
